import type { AttributionData } from "./types"
import { env } from "@/config/env"
import { filterFalsyValues } from "@/shared/lib/object"
import { getEffectiveParam, getEffectiveParams, useConfigStore } from "@/features/config"
import { geoLocationService } from "./geo/geo-service"
import { getFacebookClickIds } from "./facebook"

const STORAGE_KEY = "bp_attribution"
const SESSION_KEY = "bp_session_id"
const ANON_KEY = "bp_anonymous_id"
const REF_KEY = "_ref"
const IDFM_KEY = "@RNMV/IDFM"

function generateId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID()
  }
  return `${Math.random().toString(36).slice(2)}_${Date.now()}`
}

/** Internal analytics anonymous id (not part of the backend attribution contract). */
export function getOrCreateAnonymousId(): string {
  try {
    let id = localStorage.getItem(ANON_KEY)
    if (!id) {
      id = generateId()
      localStorage.setItem(ANON_KEY, id)
    }
    return id
  } catch {
    return generateId()
  }
}

/** Internal analytics session id (not part of the backend attribution contract). */
export function getOrCreateSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY)
    if (!id) {
      id = generateId()
      sessionStorage.setItem(SESSION_KEY, id)
    }
    return id
  } catch {
    return generateId()
  }
}

/** Read a funnel param from the merged config + URL source. */
function getParam(key: string): string | null {
  if (typeof window === "undefined") return null
  return getEffectiveParam(key)
}

/**
 * The current location with the effective funnel params (config params, which
 * bootstrap strips from the URL, overlaid by live URL params) plus the config
 * id re-attached as the query string — so the reported source URL carries the
 * full campaign context.
 */
function buildEventSourceUrl(): string {
  const { origin, pathname } = window.location
  const params = new URLSearchParams(getEffectiveParams())
  const configId = useConfigStore.getState().config?.id
  if (configId) params.set("config", configId)
  const query = params.toString()
  return query ? `${origin}${pathname}?${query}` : `${origin}${pathname}`
}

/** Persist the first cross-host referrer and return it. */
function getReferrer(): string | null {
  try {
    if (document.referrer) {
      const ref = new URL(document.referrer)
      if (ref.host !== window.location.host && !localStorage.getItem(REF_KEY)) {
        localStorage.setItem(REF_KEY, ref.toString())
      }
    }
    return localStorage.getItem(REF_KEY)
  } catch {
    return null
  }
}

function emptyAttribution(): AttributionData {
  return {
    project: null,
    campaign_id: null,
    campaign_name: null,
    adset_id: null,
    adset_name: null,
    ad_id: null,
    ad_name: null,
    media_source: null,
    config: null,
    fb_pixel: null,
    fbc: null,
    fbp: null,
    referer: null,
    event_source_url: null,
    client_user_agent: null,
    idfm: null,
    country_code: null,
    client_ip_address: null,
  }
}

function buildAttribution(): AttributionData {
  const ua = navigator.userAgent ?? ""
  const { fbc, fbp } = getFacebookClickIds()

  let idfm = getParam("_idfm")

  try {
    idfm = idfm ?? localStorage.getItem(IDFM_KEY)
  } catch {
    /* ignore */
  }

  return {
    project: env.VITE_SUBSCRIPTION_BUNDLE || null,
    campaign_id: getParam("campaign_id"),
    campaign_name: getParam("campaign_name"),
    adset_id: getParam("adset_id"),
    adset_name: getParam("adset_name"),
    ad_id: getParam("ad_id"),
    ad_name: getParam("ad_name"),
    media_source: getParam("media_source"),
    config: useConfigStore.getState().config?.id ?? getParam("config"),
    fb_pixel: getParam("fb"),
    fbc: fbc,
    fbp: getParam("_fbp") ?? fbp,
    referer: getReferrer(),
    event_source_url: buildEventSourceUrl(),
    client_user_agent: ua,
    idfm: idfm,
    country_code: null,
    client_ip_address: null,
  }
}

export function saveAttributionData(): void {
  if (typeof window === "undefined") return

  try {
    if (sessionStorage.getItem(STORAGE_KEY)) return
  } catch {
    /* proceed */
  }

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(buildAttribution()))
  } catch {
    /* ignore */
  }
}

/** Enrich the stored attribution with country + IP from the geo lookup. */
export async function enrichAttributionWithGeo(): Promise<void> {
  if (typeof window === "undefined") return

  try {
    const { ip, country } = await geoLocationService.getLocation()

    if (!ip && !country) return

    const current = getAttributionData()

    const next: AttributionData = {
      ...current,
      country_code: country ?? current.country_code,
      client_ip_address: ip ?? current.client_ip_address,
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    /* ignore */
  }
}

export function getAttributionData(): AttributionData {
  if (typeof window === "undefined") return emptyAttribution()

  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (raw) return { ...emptyAttribution(), ...JSON.parse(raw) }
  } catch {
    /* fallback to live */
  }

  return buildAttribution()
}

/** Attribution ready to send to the backend — falsy fields stripped. */
export function getAttributionPayload(): Partial<AttributionData> {
  return filterFalsyValues(getAttributionData())
}

export function getQueryString(): string {
  if (typeof window === "undefined") return ""
  return window.location.search
}

export function appendQueryString(path: string): string {
  const search = getQueryString()
  if (!search) return path
  return path.includes("?") ? `${path}&${search.slice(1)}` : `${path}${search}`
}
